import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { UploadSimple, MagnifyingGlass, Trash, Prohibit, EnvelopeSimple, Warning, Users, PaperPlaneTilt, PencilSimple, Plus, CheckCircle } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "../ui/dialog";

const field = "rounded-none border-navy/20 bg-white h-10 focus-visible:ring-2 focus-visible:ring-gold focus-visible:border-gold";

export const PlayersPanel = () => {
  const [f, setF] = useState({ q: "", gender: "", assn: "", club: "", min_age: "", max_age: "", banned: "" });
  const [data, setData] = useState({ items: [], total: 0, page: 1, assns: [] });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [mode, setMode] = useState("replace");
  const [view, setView] = useState("all");
  const [missing, setMissing] = useState({ items: [], total: 0, emailable: 0, no_email: 0, by_field: {} });
  const [missingLoading, setMissingLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [bulkSending, setBulkSending] = useState(false);
  const [dbTotal, setDbTotal] = useState(0);
  const [banned, setBanned] = useState({ items: [], total: 0 });
  const [bannedLoading, setBannedLoading] = useState(false);

  const loadDbTotal = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/players?limit=1");
      setDbTotal(data.total);
    } catch (e) { /* non-critical */ }
  }, []);

  const loadBanned = useCallback(async () => {
    setBannedLoading(true);
    try {
      const { data } = await api.get("/admin/players?banned=true&limit=200");
      setBanned({ items: data.items, total: data.total });
    } catch (e) { toast.error("Failed to load banned players"); }
    finally { setBannedLoading(false); }
  }, []);

  const loadMissing = useCallback(async () => {
    setMissingLoading(true);
    try {
      const { data } = await api.get("/admin/players/missing-data");
      setMissing(data);
    } catch (e) { toast.error("Failed to load missing-data report"); }
    finally { setMissingLoading(false); }
  }, []);

  const requestOne = async (p) => {
    setSendingId(p.id);
    try {
      await api.post(`/admin/players/${p.id}/request-data`);
      toast.success(`Request emailed to ${p.name}`);
      loadMissing();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSendingId(null); }
  };

  const requestBulk = async () => {
    if (!window.confirm(`Email a data request to all ${missing.emailable} members who have an email address on file?`)) return;
    setBulkSending(true);
    try {
      const { data } = await api.post("/admin/players/request-data-bulk");
      toast.success(`Sent ${data.sent} emails${data.failed ? `, ${data.failed} failed` : ""}${data.no_email_skipped ? `, ${data.no_email_skipped} had no email` : ""}`);
      loadMissing();
    } catch (e) { toast.error("Bulk send failed"); }
    finally { setBulkSending(false); }
  };

  const FIELD_LABEL = { dob: "DOB", gender: "Gender", email: "Email", phone: "Phone" };

  const EMPTY = { name: "", known_by: "", imr: "", dob: "", gender: "", assn_code: "", club: "", email: "", phone: "", singles_category: "", points: "" };
  const [editing, setEditing] = useState(null); // null | {..player, _new?:true}
  const [saving, setSaving] = useState(false);

  const openAdd = () => setEditing({ ...EMPTY, assn_code: "TM", _new: true });
  const openEdit = (p) => setEditing({
    ...EMPTY, ...p,
    dob: p.dob && p.dob.includes("-") ? p.dob : (p.dob || ""),
  });

  const saveForm = async () => {
    if (!editing.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    const payload = {
      name: editing.name, known_by: editing.known_by, imr: editing.imr,
      dob: editing.dob, gender: editing.gender, assn_code: editing.assn_code,
      club: editing.club, email: editing.email, phone: editing.phone,
      singles_category: editing.singles_category, points: editing.points,
    };
    try {
      if (editing._new) {
        await api.post("/admin/players", payload);
        toast.success("Player added");
      } else {
        await api.put(`/admin/players/${editing.id}`, payload);
        toast.success("Player updated");
      }
      setEditing(null);
      load(page); loadDbTotal();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const ef = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, limit: 100 });
    Object.entries(f).forEach(([k, v]) => { if (v !== "") params.append(k, v); });
    try {
      const { data } = await api.get(`/admin/players?${params}`);
      setData(data); setPage(p);
    } catch (e) { toast.error("Failed to load players"); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => { load(1); loadDbTotal(); loadBanned(); }, [load, loadDbTotal, loadBanned]);

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/players/import?mode=${mode}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} players (total ${data.total})`);
      load(1); loadDbTotal(); loadBanned();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name} (IMR ${p.imr})?`)) return;
    try { await api.delete(`/admin/players/${p.id}`); load(page); loadDbTotal(); loadBanned(); toast.success("Deleted"); }
    catch (e) { toast.error("Delete failed"); }
  };

  const toggleBan = async (p, isBanned) => {
    try { await api.post(`/admin/players/${p.id}/ban`, { banned: isBanned }); load(page); loadBanned(); toast.success(isBanned ? "Player banned" : "Ban removed"); }
    catch (e) { toast.error("Update failed"); }
  };

  const setBanExpiry = async (p, ban_expiry) => {
    try { await api.post(`/admin/players/${p.id}/ban`, { banned: true, ban_expiry }); load(page); loadBanned(); }
    catch (e) { toast.error("Update failed"); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / 100));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="players-panel">
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setView("all")} data-testid="players-view-all"
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-sm font-semibold text-sm transition-colors ${view === "all" ? "bg-navy text-white" : "bg-white border border-navy/20 text-navy hover:border-gold"}`}>
          <Users size={18} /> All players
        </button>
        <button onClick={() => { setView("missing"); loadMissing(); }} data-testid="players-view-missing"
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-sm font-semibold text-sm transition-colors ${view === "missing" ? "bg-navy text-white" : "bg-white border border-navy/20 text-navy hover:border-gold"}`}>
          <Warning size={18} /> Missing-data report{missing.total ? ` (${missing.total})` : ""}
        </button>
        <button onClick={() => { setView("banned"); loadBanned(); }} data-testid="players-view-banned"
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-sm font-semibold text-sm transition-colors ${view === "banned" ? "bg-navy text-white" : "bg-white border border-navy/20 text-navy hover:border-gold"}`}>
          <Prohibit size={18} /> Banned players{banned.total ? ` (${banned.total})` : ""}
        </button>
      </div>

      {view === "all" && (<>
      <div className="bg-white border border-navy/10 p-6">
        <h3 className="font-heading text-xl text-navy mb-1 flex items-center gap-2"><UploadSimple size={22} /> Import TTNZ player list</h3>
        <p className="text-sm text-navy/60 mb-4">Upload the TTNZ export (.xlsx or .csv). It auto-detects the right sheet and the IMR / Member ID column.</p>
        <div className="flex flex-wrap items-center gap-3">
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="rounded-none border border-navy/20 bg-white h-10 px-3 text-sm">
            <option value="replace">Replace all imported players</option>
            <option value="append">Add to existing</option>
          </select>
          <label className="cursor-pointer inline-flex items-center gap-2 bg-navy text-white font-semibold px-5 py-2.5 rounded-sm hover:bg-gold hover:text-navy transition-colors">
            <UploadSimple size={18} /> {importing ? "Importing…" : "Choose file & import"}
            <input type="file" accept=".xlsx,.csv" className="hidden" onChange={doImport} disabled={importing} data-testid="players-import-input" />
          </label>
          <span className="text-sm text-navy/50">{dbTotal.toLocaleString()} players in database</span>
        </div>
        <div className="mt-4 pt-4 border-t border-navy/10">
          <button onClick={openAdd} data-testid="player-add-btn" className="inline-flex items-center gap-2 bg-gold text-navy font-semibold px-5 py-2.5 rounded-sm hover:bg-navy hover:text-white transition-colors">
            <Plus size={18} weight="bold" /> Add a player record
          </button>
        </div>
      </div>

      <div className="bg-white border border-navy/10 p-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <Input placeholder="Name / IMR / email" value={f.q} onChange={set("q")} className={`${field} lg:col-span-2`} data-testid="players-search" />
          <select value={f.gender} onChange={set("gender")} className="rounded-none border border-navy/20 bg-white h-10 px-3 text-sm" data-testid="players-gender">
            <option value="">Any gender</option><option value="M">Male</option><option value="F">Female</option>
          </select>
          <select value={f.assn} onChange={set("assn")} className="rounded-none border border-navy/20 bg-white h-10 px-3 text-sm" data-testid="players-assn">
            <option value="">Any assn</option>
            {data.assns.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <Input placeholder="Min age" type="number" value={f.min_age} onChange={set("min_age")} className={field} data-testid="players-minage" />
          <Input placeholder="Max age" type="number" value={f.max_age} onChange={set("max_age")} className={field} data-testid="players-maxage" />
        </div>
        <div className="flex items-center gap-3 mt-3">
          <button onClick={() => load(1)} data-testid="players-search-btn" className="inline-flex items-center gap-2 bg-navy text-white font-semibold px-5 py-2.5 rounded-sm hover:bg-gold hover:text-navy transition-colors">
            <MagnifyingGlass size={18} /> Search
          </button>
          <label className="flex items-center gap-2 text-sm text-navy/70">
            <input type="checkbox" checked={f.banned === "true"} onChange={(e) => setF({ ...f, banned: e.target.checked ? "true" : "" })} /> Banned only
          </label>
          <span className="text-sm text-navy/50 ml-auto">{data.total.toLocaleString()} results</span>
        </div>
      </div>

      <div className="bg-white border border-navy/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-navy text-white/90 text-left text-xs uppercase tracking-wide">
            <tr>{["IMR", "Name", "DOB", "Age", "Gender", "Assn", "Club", "Consent", "Banned", ""].map((h) => <th key={h} className="px-3 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="px-3 py-8 text-center text-navy/50">Loading…</td></tr>}
            {!loading && data.items.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-navy/50">No players match.</td></tr>}
            {!loading && data.items.map((p) => (
              <tr key={p.id} className={`border-t border-navy/10 ${p.banned ? "bg-red-50" : ""}`} data-testid="player-row">
                <td className="px-3 py-2 font-mono text-navy/70">{p.imr}</td>
                <td className="px-3 py-2 font-medium text-navy">{p.name}{p.banned && <span className="ml-2 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">BANNED</span>}</td>
                <td className="px-3 py-2 text-navy/60">{p.dob || "—"}</td>
                <td className="px-3 py-2 text-navy/60">{p.age ?? "—"}</td>
                <td className="px-3 py-2">{p.gender}</td>
                <td className="px-3 py-2">{p.assn_code}</td>
                <td className="px-3 py-2 text-navy/60">{p.club}</td>
                <td className="px-3 py-2 whitespace-nowrap" data-testid="player-consent">
                  {p.agreed_privacy ? (
                    <span className="inline-flex items-center gap-1 text-green-700" title={p.agreed_privacy_at ? `Agreed ${new Date(p.agreed_privacy_at).toLocaleString()}` : "Agreed"}>
                      <CheckCircle size={16} weight="fill" />
                      <span className="text-xs">{p.agreed_privacy_at ? new Date(p.agreed_privacy_at).toLocaleDateString() : "Agreed"}</span>
                    </span>
                  ) : <span className="text-navy/30" title="No recorded consent">—</span>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!p.banned} onChange={(e) => toggleBan(p, e.target.checked)} data-testid="player-ban-check" className="h-4 w-4 accent-red-500" title="Mark player as banned" />
                    {p.banned && (
                      <input type="date" value={p.ban_expiry || ""} onChange={(e) => setBanExpiry(p, e.target.value)} data-testid="player-ban-expiry" title="Ban expiry (optional)" className="border border-navy/20 rounded-sm text-xs px-1.5 py-1" />
                    )}
                  </label>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {p.assn_code === "TM" ? (<>
                    <button onClick={() => openEdit(p)} data-testid="player-edit" title="Edit" className="mr-2 text-navy/40 hover:text-gold"><PencilSimple size={18} /></button>
                    <button onClick={() => del(p)} data-testid="player-delete" title="Delete" className="text-navy/40 hover:text-red-500"><Trash size={18} /></button>
                  </>) : (
                    <span className="text-xs text-navy/30 italic" title="National TTNZ reference record — read only">Reference</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-center gap-4">
        <button disabled={page <= 1} onClick={() => load(page - 1)} className="px-4 py-2 border border-navy/20 rounded-sm text-sm disabled:opacity-40">Prev</button>
        <span className="text-sm text-navy/60">Page {page} of {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => load(page + 1)} className="px-4 py-2 border border-navy/20 rounded-sm text-sm disabled:opacity-40">Next</button>
      </div>
      </>)}

      {view === "missing" && (
        <div className="space-y-6" data-testid="players-missing-view">
          <div className="bg-white border border-navy/10 p-6">
            <h3 className="font-heading text-xl text-navy mb-1 flex items-center gap-2"><Warning size={22} /> Members with missing data</h3>
            <p className="text-sm text-navy/60 mb-4">New Te Manawa members whose record is missing a date of birth, gender, email or phone. Email a request to fill in the gaps — individually or in bulk.</p>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-sm bg-navy/5 text-navy px-3 py-1.5 rounded-sm">{missing.total} incomplete</span>
              <span className="text-sm bg-navy/5 text-navy px-3 py-1.5 rounded-sm">{missing.emailable} contactable by email</span>
              <span className="text-sm bg-red-50 text-red-600 px-3 py-1.5 rounded-sm">{missing.no_email} no email on file</span>
              {Object.entries(missing.by_field || {}).filter(([, n]) => n > 0).map(([f, n]) => (
                <span key={f} className="text-xs bg-gold/15 text-navy px-2.5 py-1 rounded-sm">{FIELD_LABEL[f] || f}: {n}</span>
              ))}
            </div>
            <button onClick={requestBulk} disabled={bulkSending || missing.emailable === 0} data-testid="players-bulk-email"
              className="inline-flex items-center gap-2 bg-gold text-navy font-semibold px-5 py-2.5 rounded-sm hover:bg-navy hover:text-white transition-colors disabled:opacity-40">
              <PaperPlaneTilt size={18} /> {bulkSending ? "Sending…" : `Email all ${missing.emailable} contactable members`}
            </button>
          </div>

          <div className="bg-white border border-navy/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-navy text-white/90 text-left text-xs uppercase tracking-wide">
                <tr>{["Name", "Club", "Missing", "Email", "Last request", ""].map((h) => <th key={h} className="px-3 py-3 font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {missingLoading && <tr><td colSpan={6} className="px-3 py-8 text-center text-navy/50">Loading…</td></tr>}
                {!missingLoading && missing.items.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-navy/50">No members are missing data. 🎉</td></tr>}
                {!missingLoading && missing.items.map((p) => (
                  <tr key={p.id} className="border-t border-navy/10" data-testid="missing-row">
                    <td className="px-3 py-2 font-medium text-navy">{p.name}{p.known_by ? <span className="text-navy/40"> ({p.known_by})</span> : ""}</td>
                    <td className="px-3 py-2 text-navy/60">{p.club || "—"}</td>
                    <td className="px-3 py-2">
                      <span className="flex flex-wrap gap-1">
                        {(p.missing || []).map((m) => <span key={m} className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded">{FIELD_LABEL[m] || m}</span>)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-navy/60">{p.email || <span className="text-red-500 text-xs">none</span>}</td>
                    <td className="px-3 py-2 text-navy/50 text-xs">{p.data_email_sent_at ? new Date(p.data_email_sent_at).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {p.email ? (
                        <button onClick={() => requestOne(p)} disabled={sendingId === p.id} data-testid="missing-email-btn"
                          className="inline-flex items-center gap-1.5 text-navy border border-navy/20 px-3 py-1.5 rounded-sm text-xs font-semibold hover:bg-navy hover:text-white transition-colors disabled:opacity-40">
                          <EnvelopeSimple size={15} /> {sendingId === p.id ? "Sending…" : "Email request"}
                        </button>
                      ) : <span className="text-xs text-navy/40">No email</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === "banned" && (
        <div className="space-y-6" data-testid="players-banned-view">
          <div className="bg-white border border-navy/10 p-6">
            <h3 className="font-heading text-xl text-navy mb-1 flex items-center gap-2"><Prohibit size={22} /> Banned players</h3>
            <p className="text-sm text-navy/60">Players currently marked as banned, across all associations, with their ban-expiry date. Update the expiry or lift a ban directly here.</p>
            <span className="inline-block mt-3 text-sm bg-red-50 text-red-600 px-3 py-1.5 rounded-sm">{banned.total} banned</span>
          </div>

          <div className="bg-white border border-navy/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-navy text-white/90 text-left text-xs uppercase tracking-wide">
                <tr>{["Name", "IMR", "Assn", "Club", "Ban expires", ""].map((h) => <th key={h} className="px-3 py-3 font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {bannedLoading && <tr><td colSpan={6} className="px-3 py-8 text-center text-navy/50">Loading…</td></tr>}
                {!bannedLoading && banned.items.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-navy/50">No players are currently banned.</td></tr>}
                {!bannedLoading && banned.items.map((p) => {
                  const expired = p.ban_expiry && p.ban_expiry < new Date().toISOString().slice(0, 10);
                  return (
                    <tr key={p.id} className="border-t border-navy/10 bg-red-50/40" data-testid="banned-row">
                      <td className="px-3 py-2 font-medium text-navy">{p.name}</td>
                      <td className="px-3 py-2 font-mono text-navy/60">{p.imr || "—"}</td>
                      <td className="px-3 py-2">{p.assn_code}</td>
                      <td className="px-3 py-2 text-navy/60">{p.club || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <input type="date" value={p.ban_expiry || ""} onChange={(e) => setBanExpiry(p, e.target.value)} data-testid="banned-expiry" className="border border-navy/20 rounded-sm text-xs px-1.5 py-1" />
                        {p.ban_expiry ? (
                          <span className={`ml-2 text-xs ${expired ? "text-navy/40" : "text-red-600"}`}>{expired ? "(expired)" : "active"}</span>
                        ) : <span className="ml-2 text-xs text-navy/40">no end date</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button onClick={() => toggleBan(p, false)} data-testid="banned-unban-btn"
                          className="inline-flex items-center gap-1.5 text-navy border border-navy/20 px-3 py-1.5 rounded-sm text-xs font-semibold hover:bg-navy hover:text-white transition-colors">
                          Lift ban
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="sm:max-w-2xl rounded-none" data-testid="player-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-navy">{editing?._new ? "Add a player record" : "Edit player record"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid sm:grid-cols-2 gap-4 py-2">
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Full name *</label>
                <Input value={editing.name} onChange={ef("name")} className={field} data-testid="pf-name" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Known by</label>
                <Input value={editing.known_by} onChange={ef("known_by")} className={field} data-testid="pf-knownby" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Member number (IMR)</label>
                <Input value={editing.imr} onChange={ef("imr")} className={field} data-testid="pf-imr" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Date of birth (DD/MM/YYYY)</label>
                <Input value={editing.dob} onChange={ef("dob")} placeholder="1990-05-21 or 21/05/1990" className={field} data-testid="pf-dob" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Gender</label>
                <select value={editing.gender} onChange={ef("gender")} className="w-full rounded-none border border-navy/20 bg-white h-10 px-3 text-sm" data-testid="pf-gender">
                  <option value="">—</option><option value="M">Male</option><option value="F">Female</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Association code</label>
                {editing._new ? (
                  <Input value="TM (Te Manawa)" disabled className={`${field} bg-navy/5`} data-testid="pf-assn" />
                ) : (
                  <Input value={editing.assn_code} onChange={ef("assn_code")} placeholder="e.g. TM, C, WK" className={field} data-testid="pf-assn" />
                )}
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Club</label>
                <Input value={editing.club} onChange={ef("club")} className={field} data-testid="pf-club" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Email</label>
                <Input value={editing.email} onChange={ef("email")} className={field} data-testid="pf-email" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Phone</label>
                <Input value={editing.phone} onChange={ef("phone")} className={field} data-testid="pf-phone" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Singles category</label>
                <Input value={editing.singles_category} onChange={ef("singles_category")} className={field} data-testid="pf-singles" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Points</label>
                <Input value={editing.points} onChange={ef("points")} className={field} data-testid="pf-points" />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="px-5 py-2.5 border border-navy/20 rounded-sm text-sm font-semibold text-navy hover:bg-navy/5">Cancel</button>
            <button onClick={saveForm} disabled={saving} data-testid="pf-save" className="px-5 py-2.5 bg-navy text-white rounded-sm text-sm font-semibold hover:bg-gold hover:text-navy transition-colors disabled:opacity-40">
              {saving ? "Saving…" : (editing?._new ? "Add player" : "Save changes")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
